/**
 * CleanersCommandCenterPage — Iter-102 · Item 2.
 *
 * Consumes:
 *   GET /api/cleaners/service-lines      · 9-tile summary board
 *   GET /api/cleaners/board/{type}       · full per-line board
 *   GET /api/cleaners/live-routes        · live crew positions (30s poll)
 *   GET /api/cleaners/delayed-alerts     · escalation feed
 *   GET /api/cleaners/gate-code-audit    · masked audit log
 *   GET /api/cleaners/alerts/config      · Twilio SMS toggle
 *
 * Unified Filter is read via useUnifiedFilter(); every query re-fires
 * on state/city changes (Item 3).
 *
 * No Valet data is fetched here — only /api/cleaners/* — so Valet Command
 * Center remains untouched per permanent guardrail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Radio, AlertTriangle, Trophy, Bot, ClipboardList, Shield, Bell } from 'lucide-react';
import { apiSafeFetch } from '../lib/apiFetch';
import UnifiedGeoRoleFilter, { useBoardFilter } from '../components/UnifiedGeoRoleFilter';
import CleanersDemoAccountsCard from '../components/admin/CleanersDemoAccountsCard';

const LINE_ICON = {
  office: '🏬', retail: '🛍️', gas_station: '⛽',
  school: '🏫', medical: '🏥', warehouse: '🏭',
  restaurant: '🍽️', mall: '🏦', church: '⛪',
  bank: '🏦', stadium: '🏟️', airport: '✈️',
};

export default function CleanersCommandCenterPage() {
  // Iter-143 · FIX 4 — Redirect gate wraps the actual command center so
  // hooks below are called unconditionally.
  const { user } = useAuth();
  const fieldRole = (user?.role || '').toLowerCase();
  if (['manager', 'supervisor', 'crew_leader'].includes(fieldRole)) {
    return <Navigate to="/staff-app?tab=cleaners" replace />;
  }
  return <CleanersCommandCenterInner />;
}

function CleanersCommandCenterInner() {
  // Iter-105 · Item 1 — per-board filter instance for the Cleaners CC.
  // Force view_as=cleaners since the board only serves cleaner data.
  const boardFilter = useBoardFilter('cleaners-cc', { defaultViewAs: 'cleaners' });
  const { filter, windowDays, refreshTick } = { filter: boardFilter.filter, windowDays: boardFilter.windowDays, refreshTick: boardFilter.refreshTick };
  const scope = useMemo(() => {
    const p = new URLSearchParams();
    if (filter?.state) p.set('state', filter.state);
    if (filter?.city) p.set('city', filter.city);
    if (filter?.region) p.set('region', filter.region);
    if (filter?.date_from) p.set('date_from', filter.date_from);
    if (filter?.date_to) p.set('date_to', filter.date_to);
    if (windowDays) p.set('window_days', String(windowDays));
    return p.toString();
  }, [filter?.state, filter?.city, filter?.region, filter?.date_from, filter?.date_to, windowDays]);

  const [lines, setLines] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [board, setBoard] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [delayed, setDelayed] = useState([]);
  const [audit, setAudit] = useState([]);
  const [alertConfig, setAlertConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Summary tiles (9 lines).
  const loadSummary = useCallback(async () => {
    const { data } = await apiSafeFetch(`/api/cleaners/service-lines?${scope}`);
    if (data?.service_lines) {
      setLines(data.service_lines);
      if (!activeType && data.service_lines.length) {
        setActiveType(data.service_lines[0].property_type);
      }
    }
    setLoading(false);
  }, [scope, activeType]);

  useEffect(() => { loadSummary(); }, [loadSummary, refreshTick]);

  // Board detail — re-fires when active type or scope changes.
  useEffect(() => {
    if (!activeType) return;
    let alive = true;
    (async () => {
      const [b, r, d, a, cfg] = await Promise.all([
        apiSafeFetch(`/api/cleaners/board/${activeType}?${scope}`),
        apiSafeFetch(`/api/cleaners/live-routes?property_type=${activeType}&${scope}`),
        apiSafeFetch(`/api/cleaners/delayed-alerts?${scope}`),
        apiSafeFetch(`/api/cleaners/gate-code-audit?${scope}`),
        apiSafeFetch('/api/cleaners/alerts/config'),
      ]);
      if (!alive) return;
      setBoard(b.data || null);
      setRoutes(r.data?.routes || []);
      setDelayed(d.data?.delayed_routes || []);
      setAudit(a.data?.rows || []);
      setAlertConfig(cfg.data || null);
    })();
    return () => { alive = false; };
  }, [activeType, scope, refreshTick]);

  // 30-second live-routes poll.
  useEffect(() => {
    if (!activeType) return;
    const tick = async () => {
      const { data } = await apiSafeFetch(`/api/cleaners/live-routes?property_type=${activeType}&${scope}`);
      if (data?.routes) setRoutes(data.routes);
    };
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [activeType, scope]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="cleaners-command-center-page">
      {/* Iter-105 · Item 1 — Per-board filter (admin variant for admin
          roles, field variant for staff). Placed above the board
          content. */}
      <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix="unified-filter-cleaners-cc" />

      {/* Iter-111 · Fix 3 — 9 self-contained Cleaners commercial demo
          accounts (admin-only; card self-hides on 403). */}
      <CleanersDemoAccountsCard />
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] mb-1 font-bold" style={{ color: '#22d3ee' }}>
          Cleaners HQ
        </p>
        <h1 className="text-3xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="cleaners-cc-heading">
          CLEANERS COMMAND CENTER
        </h1>
        <p className="text-xs text-zinc-400 mt-1">
          Nine branded service-line boards · live routes, violations, leaderboards, and audit logs — cleaner data only.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading service lines…</p>
      ) : (
        <>
          {/* Nine-tile summary board */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cleaners-cc-summary-grid">
            {lines.map((l) => {
              const active = l.property_type === activeType;
              return (
                <button
                  key={l.property_type}
                  type="button"
                  onClick={() => setActiveType(l.property_type)}
                  className="text-left p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{
                    backgroundColor: active ? 'rgba(34, 211, 238, 0.12)' : '#1f1f1f',
                    border: `2px solid ${active ? '#22d3ee' : '#3f3f46'}`,
                  }}
                  data-testid={`cleaners-cc-tile-${l.property_type}`}
                  data-active={active}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl">{LINE_ICON[l.property_type] || '🏢'}</span>
                    <Badge className="bg-zinc-800 text-zinc-300">{l.property_count} props</Badge>
                  </div>
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#22d3ee' }}>
                    {l.necs_name}
                  </p>
                  <p className="text-sm text-white leading-tight mt-1">{l.property_label}</p>
                  <div className="grid grid-cols-3 gap-1 mt-3 text-center">
                    <SmallStat label="Active" value={l.active_routes} />
                    <SmallStat label="Viol · wk" value={l.weekly_violations} tone={l.weekly_violations > 0 ? 'red' : 'green'} />
                    <SmallStat label="Top" value={l.top_performer?.routes_completed ?? 0} tone="cyan" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected board detail */}
          {board && activeType && (
            <BoardDetail
              board={board}
              routes={routes}
              delayed={delayed}
              audit={audit}
              alertConfig={alertConfig}
            />
          )}
        </>
      )}
    </div>
  );
}

function SmallStat({ label, value, tone }) {
  const c = tone === 'red' ? '#f87171' : tone === 'green' ? '#4ade80' : tone === 'cyan' ? '#22d3ee' : '#e5e5e5';
  return (
    <div className="rounded px-1.5 py-1" style={{ backgroundColor: '#0f0f0f' }}>
      <p className="text-[8px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-sm font-black" style={{ color: c }}>{value}</p>
    </div>
  );
}

function BoardDetail({ board, routes, delayed, audit, alertConfig }) {
  return (
    <div className="space-y-4" data-testid={`cleaners-cc-board-${board.property_type}`}>
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#22d3ee' }}>
              {board.necs_name}
            </p>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {board.property_label?.toUpperCase()}
            </h2>
          </div>
          <div className="flex gap-2">
            <Metric icon={<Radio className="h-4 w-4" />} label="Active" value={board.active_routes ?? 0} accent="#22d3ee" />
            <Metric icon={<AlertTriangle className="h-4 w-4" />} label="Violations" value={board.violations?.length ?? 0} accent="#f87171" />
            <Metric icon={<Bot className="h-4 w-4" />} label="AI Acc" value={board.ai_accuracy?.accuracy_pct != null ? `${board.ai_accuracy.accuracy_pct}%` : '—'} accent="#a78bfa" />
          </div>
        </div>
      </Card>

      {/* Property cards */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-properties">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold" style={{ color: '#22d3ee' }}>
          Properties in this line
        </h3>
        {(board.properties || []).length === 0 ? (
          <p className="text-xs text-zinc-500">No properties yet in this service line for the selected scope.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {board.properties.map((p) => (
              <div key={p.property_id} className="rounded-lg p-3" style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46' }}>
                <p className="text-sm font-bold">{p.property_name}</p>
                <p className="text-[11px] text-zinc-500">{p.city}, {p.state} · Tier: {p.tier || '—'}</p>
                <p className="text-[10px] text-zinc-500 mt-1">Gate: ••• · Amenities: {(p.amenities || []).length}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Live routes */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-live-routes">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold" style={{ color: '#22d3ee' }}>
          Live crew positions ({routes.length})
        </h3>
        {routes.length === 0 ? (
          <p className="text-xs text-zinc-500">No active routes in the current window.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {routes.slice(0, 20).map((r, i) => (
              <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                <span>{r.agent_name || r.agent_id} · {r.property_name}</span>
                <Badge className="bg-zinc-800">{r.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Delayed alerts */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-delayed-alerts">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold flex items-center gap-2" style={{ color: '#f87171' }}>
          <AlertTriangle className="h-4 w-4" /> Delayed routes ({delayed.length})
        </h3>
        {delayed.length === 0 ? (
          <p className="text-xs text-zinc-500">All routes on schedule.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {delayed.slice(0, 10).map((d, i) => (
              <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                <span>{d.property_name}</span>
                <Badge className="bg-red-500/20 text-red-200 border border-red-500">
                  Escalate → {d.escalation_level}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Leaderboard */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-leaderboard">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold flex items-center gap-2" style={{ color: '#fbbf24' }}>
          <Trophy className="h-4 w-4" /> Leaderboard
        </h3>
        {(board.leaderboard || []).length === 0 ? (
          <p className="text-xs text-zinc-500">No rankings yet — leaderboard populates as crews log service summaries.</p>
        ) : (
          <ol className="space-y-1 text-xs">
            {board.leaderboard.map((r, i) => (
              <li key={r.user_id} className="flex justify-between border-b border-zinc-800 py-1">
                <span>{i + 1}. {r.name} · {r.badge_id}</span>
                <span className="font-mono">{r.score}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* Service summaries */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-service-summaries">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold flex items-center gap-2" style={{ color: '#22d3ee' }}>
          <ClipboardList className="h-4 w-4" /> Recent service summaries
        </h3>
        {(board.service_summaries || []).length === 0 ? (
          <p className="text-xs text-zinc-500">No submissions in the last 7 days.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {board.service_summaries.slice(0, 20).map((s, i) => (
              <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                <span>{s.date} · {s.property_name} · {s.agent_name}</span>
                <span className="text-zinc-400">{s.quantity} units</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Gate code audit */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-gate-audit">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold flex items-center gap-2" style={{ color: '#22d3ee' }}>
          <Shield className="h-4 w-4" /> Gate code audit (masked)
        </h3>
        {audit.length === 0 ? (
          <p className="text-xs text-zinc-500">No entries.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {audit.slice(0, 12).map((r, i) => (
              <li key={i} className="flex justify-between border-b border-zinc-800 py-1">
                <span>{r.property_name}</span>
                <span className="font-mono">{r.gate_code_masked}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Alert config — Iter-105 · Item 6 · Slack webhook fallback */}
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white" data-testid="cleaners-cc-alert-config">
        <h3 className="text-xs uppercase tracking-widest mb-3 font-bold flex items-center gap-2" style={{ color: '#22d3ee' }}>
          <Bell className="h-4 w-4" /> Alert configuration
        </h3>
        <p className="text-xs text-zinc-400 mb-3">
          SMS enabled: <span className="font-bold text-white">{alertConfig?.sms_enabled ? 'Yes' : 'No'}</span>
          {' · '}
          Recipients: <span className="font-bold text-white">{(alertConfig?.sms_recipients || []).length}</span>
        </p>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
            Slack webhook URL (per-region delayed-route fallback)
          </label>
          <input
            type="url"
            defaultValue={alertConfig?.slack_webhook_url || ''}
            placeholder="https://hooks.slack.com/services/..."
            onBlur={async (e) => {
              const val = e.target.value.trim() || null;
              await apiSafeFetch('/api/cleaners/alerts/config', {
                method: 'POST',
                body: { ...(alertConfig || {}), slack_webhook_url: val },
              });
            }}
            className="w-full text-xs px-2 py-1.5 rounded-md bg-zinc-800 border border-zinc-700 text-white"
            data-testid="cleaners-cc-slack-webhook"
          />
          <p className="text-[10px] text-zinc-500 mt-1">
            Fires alongside Twilio SMS on delayed-route threshold. Leave blank to disable Slack for this region.
          </p>
        </div>
      </Card>
    </div>
  );
}

function Metric({ icon, label, value, accent }) {
  return (
    <div className="text-center px-3 py-1 rounded-md" style={{ backgroundColor: `${accent}22`, border: `1px solid ${accent}` }}>
      <div className="flex items-center gap-1 justify-center" style={{ color: accent }}>
        {icon}
        <span className="text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <p className="text-sm font-black text-white">{value}</p>
    </div>
  );
}
